import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, Link, useLocation } from "react-router-dom";
import Header from "@user/components/Header";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Download, MessageSquare, Clock, Sparkles, Paperclip, FolderOpen, ClipboardList, NotebookPen, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import RatingPromptModal from "@user/components/RatingPromptModal";
import DailyCall from "@shared/consultation/DailyCall";
import { useConsultationSocket } from "@/hooks/useConsultationSocket";
import { uploadConsultationDocument } from "@shared/consultation/ConsultationDocuments";
import { MAX_FILES_PER_CONSULTATION, toPending, prepareImage } from "@shared/consultation/ComposerAttachments";
import { LawyerNotesPane } from "@shared/consultation/LawyerNotesPane";
import { SessionTimer, TimerStartHint, LawyerWaitCountdown } from "@shared/consultation/SessionTimer";
import { DocumentsPane } from "@shared/consultation/DocumentsDrawer";
import { RightPanel } from "@shared/consultation/RightPanel";
import { LawyerRoomBar } from "@shared/consultation/LawyerRoomBar";
import { AiSummaryPanel } from "@shared/consultation/AiSummaryPanel";
import { CallStage } from "@shared/consultation/CallStage";
import { PostConsultationSummary } from "@shared/consultation/PostConsultationSummary";
import { MessageBubble, TypingIndicator } from "@shared/consultation/MessageBubble";
import { Composer } from "@shared/consultation/Composer";
import { ConsultationSummaryCard } from "@shared/consultation/ConsultationSummaryCard";
import { TERMINAL, sessionState, Lock } from "@shared/consultation/roomUtils";

export { sessionState };

const SIDE_TABS = [
  { key: "summary", label: "Consultation Summary", Icon: ClipboardList },
  { key: "notes", label: "Lawyer Notes", Icon: NotebookPen },
  { key: "memo", label: "AI Memo", Icon: Sparkles },
];

export default function ConsultationRoom() {
  const { id } = useParams();
  const location = useLocation();
  const lawyerScope = location.pathname.startsWith(`${LAWYER_BASE}/`);
  const TopBar = lawyerScope ? LawyerRoomBar : Header;
  const [loadError, setLoadError] = useState(null);
  // Workspace right panel. Chat consultations never duplicate the conversation → Documents | AI Memo only.
  const [panelTab, setPanelTab] = useState(null); // null → mode default (chat for calls, documents otherwise)
  const [sideTab, setSideTab] = useState("summary"); // lawyer left panel: summary | notes | memo
  const [hadCall, setHadCall] = useState(false); // shows "Rejoin" after leaving while still live
  const [unreadChat, setUnreadChat] = useState(0);
  const seenCountRef = useRef(0);
  const [docCount, setDocCount] = useState(null);
  const panelRef = useRef(null);
  // Desktop workspace height = viewport minus whatever sits above the room (env banner etc.).
  const shellRef = useRef(null);
  const measureShell = useCallback(() => {
    const el = shellRef.current;
    if (el) el.style.setProperty("--room-top", `${Math.round(el.getBoundingClientRect().top + window.scrollY)}px`);
  }, []);
  const setShellRef = useCallback((el) => { shellRef.current = el; measureShell(); }, [measureShell]);
  useEffect(() => {
    window.addEventListener("resize", measureShell);
    return () => window.removeEventListener("resize", measureShell);
  }, [measureShell]);
  const [docsKey, setDocsKey] = useState(0);
  const [pending, setPending] = useState([]); // files chosen but not yet sent
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [editing, setEditing] = useState(null); // { id, content }
  const fileInputRef = useRef(null);
  const [sp] = useSearchParams();
  const invoiceNo = sp.get("invoice");
  const openReview = sp.get("review") === "1";
  const { user } = useAuth();
  const [consult, setConsult] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [inCall, setInCall] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(openReview);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const typingTimerRef = useRef(null);
  const otherTypingTimerRef = useRef(null);

  const isLawyerSide = user?.role === "lawyer" && consult && user?.lawyer_id === consult.lawyer_id;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/consultations/${id}`);
      if (r.data?.timer) r.data.timer.fetched_at = Date.now(); // anchor for the server-clock offset
      if (r.data?.lawyer_no_show) r.data.lawyer_no_show.fetched_at = Date.now();
      setConsult(r.data);
      setLoadError(null);
    } catch (e) {
      const st = e?.response?.status;
      setLoadError(st === 401 ? "Please sign in again to open this consultation."
        : st === 403 ? "You don't have access to this consultation."
        : st === 404 ? "This consultation could not be found."
        : "Could not load this consultation. Please try again.");
    }
  }, [id]);

  // Load history once
  const loadMessages = useCallback(async () => {
    try {
      const r = await api.get(`/consultations/${id}/messages`);
      setMessages(r.data.messages || []);
    } catch (error) { console.warn("[room] messages refresh failed", error?.response?.status || error?.message || error); }
  }, [id]);

  useEffect(() => { load(); loadMessages(); }, [load, loadMessages]);

  // WebSocket — primary realtime channel.
  const terminalStatus = TERMINAL.includes(consult?.status);
  // Closed rooms have nothing left to deliver — don't hold a socket open.
  const { connected, send, addEventListener } = useConsultationSocket(id, { enabled: !!id && !terminalStatus });

  // Resync after a WebSocket reconnect — a status/timer broadcast may have been missed while offline.
  const wasConnected = useRef(false);
  useEffect(() => {
    if (connected && !wasConnected.current && consult) load();
    wasConnected.current = connected;
  }, [connected, consult, load]);

  // Local countdown hit zero: the server decides. Refetch now and keep polling every 4 s until it reports a terminal status
  // (covers sub-second clock skew and a missed broadcast). Display never completes anything by itself.
  const [settling, setSettling] = useState(false);
  const settleExpiry = useCallback(() => { setSettling(true); load(); }, [load]);
  useEffect(() => {
    if (!settling) return undefined;
    if (!consult || TERMINAL.includes(consult.status)) { setSettling(false); return undefined; }
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [settling, consult, load]);
  const session = sessionState(connected, terminalStatus);

  useEffect(() => {
    const offMsg = addEventListener("message", (evt) => {
      const m = evt.message;
      if (!m) return;
      setMessages((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
    });
    const applyUpdate = (evt) => {
      const m = evt.message;
      if (!m) return;
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...m, attachment: m.attachment } : x)));
    };
    const offUpdated = addEventListener("message_updated", applyUpdate);
    const offDeleted = addEventListener("message_deleted", applyUpdate);
    const offTyping = addEventListener("typing", (evt) => {
      // Show "other typing" only when peer is the other party
      const myRole = user?.role === "lawyer" && consult && user?.lawyer_id === consult.lawyer_id ? "lawyer" : "user";
      if (evt.role && evt.role === myRole) return;
      setOtherTyping(true);
      if (otherTypingTimerRef.current) clearTimeout(otherTypingTimerRef.current);
      otherTypingTimerRef.current = setTimeout(() => setOtherTyping(false), 4000);
    });
    const offStatus = addEventListener("status", () => { load(); });
    const offTimer = addEventListener("timer", () => { load(); });
    const offError = addEventListener("error", (evt) => { toast.error(evt.detail || "Channel error"); });
    return () => { offMsg(); offTyping(); offUpdated(); offDeleted(); offStatus(); offTimer(); offError(); };
  }, [addEventListener, load, user, consult]);

  // Polling fallback when WS not connected (covers ingress hiccups).
  useEffect(() => {
    if (connected) return;
    const iv = setInterval(loadMessages, 4000);
    return () => clearInterval(iv);
  }, [connected, loadMessages]);

  // Scroll only the message list (not the page) so the fixed-height workspace never jumps.
  useEffect(() => {
    const list = bottomRef.current?.parentElement;
    if (list) list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  }, [messages, pending.length]);

  const saveEdit = async () => {
    if (!editing) return;
    const content = editing.content.trim();
    if (!content) return;
    try {
      const r = await api.patch(`/consultations/${id}/messages/${editing.id}`, { content });
      setMessages((prev) => prev.map((x) => (x.id === editing.id ? { ...x, ...r.data } : x)));
      setEditing(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not edit message"); }
  };
  const deleteMsg = async (m) => {
    if (!window.confirm(m.attachment ? "Delete this shared document for everyone?" : "Delete this message for everyone?")) return;
    try {
      const r = await api.delete(`/consultations/${id}/messages/${m.id}`);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...r.data, attachment: undefined } : x)));
      if (m.attachment) setDocsKey((k) => k + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete message"); }
  };

  // Queue files from the picker, a paste, or a drop — nothing uploads until Send.
  const queueFiles = (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const fresh = files.filter((f) => !pending.some((p) => p.file.name === f.name && p.file.size === f.size));
    const room = MAX_FILES_PER_CONSULTATION - (docCount || 0) - pending.length;
    if (fresh.length > room) toast.error(`Limit: ${MAX_FILES_PER_CONSULTATION} documents per consultation`);
    const added = fresh.slice(0, Math.max(0, room)).map(toPending);
    setPending((prev) => [...prev, ...added]);
    // Shrink big photos in the background; the chip shows "Optimising…" meanwhile.
    added.filter((p) => p.status === "preparing").forEach((p) => prepareImage(p).then((patch) => patchPending(p.key, patch)));
  };
  const onPickFile = (e) => { queueFiles(e.target.files); e.target.value = ""; };
  const canAttach = consult?.payment_status === "paid" && !terminalStatus;
  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length || !canAttach) return;
    e.preventDefault();
    queueFiles(files.map((f) => (f.name && f.name !== "image.png") ? f : new File([f], `Screenshot ${new Date().toISOString().slice(0, 19).replace("T", " ").replace(/:/g, "-")}.png`, { type: f.type || "image/png" })));
  };
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const dragProps = canAttach ? {
    onDragEnter: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); },
    onDragOver: (e) => { if (hasFiles(e)) e.preventDefault(); },
    onDragLeave: (e) => { if (!hasFiles(e)) return; dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); },
    onDrop: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current = 0; setDragging(false); queueFiles(e.dataTransfer.files); },
  } : {};
  const removePending = (key) => setPending((prev) => prev.filter((p) => p.key !== key));
  const patchPending = (key, patch) => setPending((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  // Upload one composer file; on success it becomes a chat message (server broadcasts it too).
  const uploadPending = async (it, caption = "") => {
    patchPending(it.key, { status: "uploading", progress: 0, error: null });
    try {
      const res = await uploadConsultationDocument(id, it.file, "chat", (pct) => patchPending(it.key, { progress: pct }), caption);
      if (res.message) setMessages((prev) => prev.some((x) => x.id === res.message.id) ? prev : [...prev, res.message]);
      setDocsKey((k) => k + 1);
      removePending(it.key);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      patchPending(it.key, { status: "error", error: err?.response?.data?.detail || "Upload failed — tap retry", fatal: [409, 413, 415].includes(status) });
      return false;
    }
  };

  const retryPending = async (key) => {
    const it = pending.find((p) => p.key === key);
    if (!it || sendingRef.current) return;
    sendingRef.current = true; setSending(true);
    try { await uploadPending(it); } finally { sendingRef.current = false; setSending(false); }
  };

  const sendText = async (content) => {
    if (connected && send({ type: "chat", content })) return;
    // Fallback to REST.
    try {
      const r = await api.post(`/consultations/${id}/messages`, { content });
      setMessages((m) => [...m, r.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  // Send = upload every queued/retriable file (in order), then the text. The text rides along as the caption of the
  // first file that uploads; it is only sent as its own bubble when no file could carry it. Guarded against double-sends.
  const sendMsg = async () => {
    if (sendingRef.current) return;
    const content = input.trim();
    const files = pending.filter((p) => p.status === "queued" || (p.status === "error" && !p.fatal));
    if (!content && !files.length) return;
    sendingRef.current = true; setSending(true);
    setInput("");
    let failed = 0;
    let caption = isLive ? content : "";
    try {
      for (const it of files) {
        if (await uploadPending(it, caption)) caption = ""; else failed += 1;
      }
      if (caption) await sendText(caption);
    } finally {
      sendingRef.current = false; setSending(false);
    }
    if (failed) toast.error(`${failed} file${failed > 1 ? "s" : ""} could not be sent — retry or remove ${failed > 1 ? "them" : "it"}.`);
  };

  const onTyping = (v) => {
    setInput(v);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      if (connected) send({ type: "typing" });
      else api.post(`/consultations/${id}/typing`).catch(() => {});
    }, 400);
  };

  // Auto-prompt rating
  useEffect(() => {
    if (!consult || !user) return;
    if (user.user_id !== consult.user_id) return;
    if (consult.status !== "completed") return;
    const dismissedKey = `adv_rated_${consult.id}`;
    if (hasReviewed || localStorage.getItem(dismissedKey)) return;
    api.get(`/consultations/${consult.id}/review`).then((r) => {
      if (!r.data?.rating) setRatingOpen(true);
      else setHasReviewed(true);
    }).catch(() => setRatingOpen(true));
  }, [consult, user, hasReviewed]);

  const startConsult = async () => {
    setBusy(true);
    try {
      await api.post(`/consultations/${id}/start`);
      toast.success("Consultation started — client notified");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setBusy(false); }
  };
  // Audio/video: the lawyer's successful Daily join starts the server timer (server validates + dedupes).
  const reportLawyerJoined = useCallback(async () => {
    try {
      const r = await api.post(`/consultations/${id}/timer/start`);
      if (r.data?.started) load();
    } catch (e) {
      if (e?.response?.status !== 409) toast.error(e?.response?.data?.detail || "Could not start the consultation timer");
    }
  }, [id, load]);

  // Lawyer's one-time extra time during system grace (server validates + dedupes).
  const giftTime = useCallback(async () => {
    try {
      const r = await api.post(`/consultations/${id}/timer/gift`);
      toast.success(`Gifted ${r.data?.gifted_minutes} extra minutes to the client`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not gift extra time");
      load();
    }
  }, [id, load]);

  // Client: one toast per grant, only after the server confirms it (gift_granted_at on the consultation); deduped across refreshes.
  useEffect(() => {
    const t = consult?.timer;
    if (isLawyerSide || !t?.gift_granted_at || consult?.status !== "live" || !["GRACE", "EXTRA"].includes(t.state)) return;
    const key = `advoset:gift-toast:${consult.id}:${t.gift_granted_at}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    toast.success(`🎁 Your lawyer has gifted you ${t.gifted_minutes} extra minutes!`, { duration: 6000 });
  }, [consult?.id, consult?.timer, consult?.status, isLawyerSide]);

  const completeConsult = async () => {
    setBusy(true);
    try {
      await api.post(`/consultations/${id}/complete`);
      toast.success("Consultation marked complete");
      // Nudge the lawyer straight into the Legal Consultation Record author.
      if (isLawyerSide) {
        toast("Now draft the Legal Consultation Record", {
          description: "Every completed consultation should have one record for the client.",
          action: {
            label: "Complete record",
            onClick: () => { window.location.href = `${LAWYER_BASE}/consultations/${id}/lcr`; },
          },
        });
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not complete"); }
    finally { setBusy(false); }
  };

  // Audio/video workspace: default the right panel to Chat, count unread while on another tab,
  // and leave the docked call when the consultation completes (lifecycle unchanged).
  const callMode = consult?.consultation_type === "audio" || consult?.consultation_type === "video";
  const activeTab = (panelTab && panelTab !== "memo") ? panelTab : (callMode ? "chat" : "documents");
  useEffect(() => {
    const n = messages.length;
    if (callMode && activeTab !== "chat" && n > seenCountRef.current) setUnreadChat((u) => u + (n - seenCountRef.current));
    if (!callMode || activeTab === "chat") setUnreadChat(0);
    seenCountRef.current = n;
  }, [messages.length, activeTab, callMode]);
  useEffect(() => {
    if (consult?.status === "completed" && inCall) { setInCall(false); toast.info("Consultation completed — call ended"); }
  }, [consult?.status, inCall]);


  if (!consult) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <TopBar />
        {loadError ? (
          <div className="max-w-3xl mx-auto p-8 text-center" data-testid="consultation-load-error">
            <p className="text-slate-700 font-bold">{loadError}</p>
            <Link to={lawyerScope ? `${LAWYER_BASE}/consultations` : "/dashboard"} className="mt-3 inline-block text-sm text-[#0A2540] underline">Go back</Link>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto p-8 text-center text-slate-500">Loading consultation…</div>
        )}
      </div>
    );
  }

  const isLive = consult.status === "live";
  const isCompleted = consult.status === "completed";
  const timerState = consult.timer?.started_at ? consult.timer.state : "NOT_STARTED";
  const isTerminal = terminalStatus;
  const isCancelled = isTerminal && !isCompleted;
  const isCall = callMode;
  const canStart = isLawyerSide && consult.payment_status === "paid"
    && !isCompleted && !isLive && !isTerminal;
  const hasLegitimateStart = !!consult.timer?.started_at && (
    (consult.consultation_type === "chat" && consult.timer.trigger === "lawyer_first_message")
    || (isCall && consult.timer.trigger === "lawyer_joined_call")
  );

  // The conversation (header + messages + composer). Chat mode: main stage. Audio/video: right-panel Chat tab.
  const conversation = (
    <div className="flex w-full min-w-0 flex-1 min-h-0 flex-col relative" data-testid="conversation-dropzone" data-dragging={dragging ? "true" : "false"} {...dragProps}>
            {dragging && (
              <div className="absolute inset-0 z-10 m-2 rounded-2xl border-2 border-dashed border-[#D4AF37] bg-[#FBF4DF]/90 grid place-items-center pointer-events-none" data-testid="drop-overlay">
                <p className="text-sm font-bold text-[#0A2540] inline-flex items-center gap-2"><Paperclip size={16} /> Drop files to attach</p>
              </div>
            )}
            <div className="box-border w-full min-w-0 shrink-0 px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between gap-2 flex-wrap" data-testid="chat-header">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <MessageSquare size={16} className="text-[#0A2540] shrink-0" />
                <p className="min-w-0 break-words text-sm font-bold text-[#0A2540] [overflow-wrap:anywhere]">
                  {isTerminal ? "Chat history" : "Direct chat"} with {isLawyerSide ? consult.user_name : consult.lawyer_name}
                </p>
              </div>
              <div className="flex w-full min-w-0 max-w-full flex-wrap items-center gap-2 sm:w-auto">
                <button type="button" onClick={() => { setPanelTab("documents"); panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }} data-testid="documents-open"
                  className="lg:hidden text-[11px] font-bold text-[#0A2540] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-white">
                  <FolderOpen size={12} /> Documents{docCount != null ? ` (${docCount})` : ""}
                </button>
                {isLive && consult.payment_status === "paid" && consult.consultation_type === "chat" && (
                  timerState === "NOT_STARTED"
                    ? consult.lawyer_no_show?.stage === "initiation"
                      ? <span className="text-[11px] font-bold text-amber-800" data-testid="chat-initiation-status">First message starts the consultation timer</span>
                      : <TimerStartHint consultationType="chat" isLawyerSide={isLawyerSide} />
                    : <SessionTimer timer={consult.timer} onExpired={settleExpiry} onGift={isLawyerSide ? giftTime : undefined} />
                )}
                {isTerminal && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-testid="chat-history-label">Transcript</span>
                )}
              </div>
            </div>

            <div className="box-border w-full min-w-0 max-w-full flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-3" data-testid="consult-messages">
              {consult.payment_status !== "paid" && !isTerminal && (
                <div className="max-w-md mx-auto text-center py-10">
                  <Lock className="mx-auto text-slate-300" />
                  <p className="mt-3 text-sm text-slate-500">Complete payment to start chatting with the lawyer.</p>
                </div>
              )}
              {!isLive && consult.payment_status === "paid" && !isTerminal && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 flex items-center gap-2" data-testid="consult-not-live-banner">
                  <Clock size={14} /> {isLawyerSide
                    ? "Press Start consultation when ready — the timer begins for the client at that moment."
                    : `Booking confirmed and paid. Waiting for ${consult.lawyer_name || "the lawyer"} to start the consultation — chat${consult.consultation_type !== "chat" ? ` and the ${consult.consultation_type} call` : ""} will unlock automatically.`}
                </div>
              )}
              {consult.payment_status === "paid" && !isTerminal && consult.consultation_type === "chat" && consult.lawyer_no_show?.applies && (
                <LawyerWaitCountdown noShow={consult.lawyer_no_show} onExpired={settleExpiry} isLawyerSide={isLawyerSide} />
              )}

              {messages.map((m) => (
                <MessageBubble key={m.id} m={m} mine={m.sender_id === user?.user_id} cid={consult.id} isCompleted={isCompleted}
                  editing={editing} setEditing={setEditing} onSaveEdit={saveEdit} onDelete={deleteMsg} />
              ))}
              {otherTyping && <TypingIndicator />}
              <div ref={bottomRef} />
            </div>

            {isTerminal && (
              <div className="shrink-0 px-5 py-3 border-t border-slate-100 bg-slate-50/60 text-xs text-slate-500 text-center" data-testid="consult-ended-note">
                This consultation has {isCompleted ? "ended" : consult.status === "lawyer_no_show"
                  ? `been closed — the lawyer ${consult.lawyer_no_show_stage === "initiation" ? "did not initiate it" : consult.lawyer_no_show_stage === "start" ? "did not start it" : "did not join"}`
                  : "been cancelled"} — the chat is read-only.
              </div>
            )}

            {consult.payment_status === "paid" && !isTerminal && (
              <Composer pending={pending} onRemove={removePending} onRetry={retryPending} sending={sending} fileInputRef={fileInputRef}
                onPickFile={onPickFile} input={input} onTyping={onTyping} onSend={sendMsg} onPaste={onPaste} isLive={isLive} isLawyerSide={isLawyerSide} />
            )}
    </div>
  );

  return (
    <div ref={setShellRef} className="box-border flex w-full max-w-full min-w-0 min-h-screen flex-col bg-[#FAFAFA] lg:h-[calc(100dvh-var(--room-top,0px))] lg:min-h-0 lg:overflow-hidden" data-testid="consultation-room-shell">
      <TopBar />

      {invoiceNo && (
        <div className="box-border flex w-full min-w-0 max-w-full flex-wrap items-center justify-center gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-center text-xs font-bold text-emerald-800" data-testid="payment-success-banner">
          <CheckCircle2 size={14} className="shrink-0" /> <span className="min-w-0 break-all">Payment successful · Invoice {invoiceNo}</span>
          <button onClick={() => window.print()} className="ml-2 underline inline-flex items-center gap-1"><Download size={12} /> Print</button>
        </div>
      )}

      {/* Desktop: the workspace is viewport-fixed (single 1fr row) so every column scrolls internally; phones stack with bounded pane heights. */}
      <div className="box-border grid w-full max-w-[1600px] min-w-0 flex-1 grid-cols-1 gap-4 px-4 py-4 mx-auto lg:min-h-0 lg:gap-6 lg:overflow-hidden lg:grid-rows-[minmax(0,1fr)] lg:grid-cols-12 lg:px-8 lg:py-6 xl:grid-cols-[280px_minmax(0,1fr)_360px]" data-testid="consultation-workspace" data-mode={consult.consultation_type}>
        {/* Left panel — user: Consultation Summary; lawyer: Summary | Lawyer Notes | AI Memo (both private) */}
        <aside className="order-2 flex w-full min-w-0 flex-col gap-3 lg:order-1 lg:col-span-3 lg:min-h-0 xl:col-span-1" data-testid="workspace-sidebar" data-tab={sideTab}>
          <Link to={isLawyerSide ? `${LAWYER_BASE}/consultations` : "/dashboard"} className="shrink-0 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A2540]">
            <ArrowLeft size={14} /> {isLawyerSide ? "All consultations" : "My consultations"}
          </Link>
          {isLawyerSide ? (
            <nav className="box-border flex w-full max-w-full min-w-0 shrink-0 gap-1 overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-200 bg-white p-1.5 lg:flex-col lg:overflow-visible" role="tablist" data-testid="sidebar-tabs">
              {SIDE_TABS.map(({ key, label, Icon }) => {
                const on = sideTab === key;
                return (
                  <button key={key} type="button" role="tab" aria-selected={on} onClick={() => setSideTab(key)} data-testid={`sidebar-tab-${key}`}
                    className={`flex flex-none items-center justify-center gap-1.5 rounded-xl border-l-[3px] px-2 py-2 text-[11px] font-bold transition-colors lg:justify-start lg:gap-2 lg:px-3 lg:text-sm ${on ? "bg-[#FBF4DF] text-[#0A2540] border-[#D4AF37]" : "text-slate-600 hover:bg-slate-50 border-transparent"}`}>
                    <Icon size={15} className={on ? "text-[#0A2540]" : "text-slate-400"} /> <span className="whitespace-nowrap">{label}</span>
                  </button>
                );
              })}
            </nav>
          ) : (
            <p className="shrink-0 px-1 text-sm font-extrabold text-[#0A2540] inline-flex items-center gap-2" style={{ fontFamily: "Poppins" }} data-testid="sidebar-summary-heading">
              <ClipboardList size={16} /> Consultation Summary
            </p>
          )}

          <div className="w-full min-w-0 flex-1 lg:min-h-0 lg:overflow-y-auto" data-testid={`sidebar-pane-${sideTab}`}>
          {sideTab === "notes" && isLawyerSide && <LawyerNotesPane cid={consult.id} />}
          {sideTab === "memo" && isLawyerSide && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="memo-pane">
              <p className="mb-3 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 inline-flex items-center gap-1" data-testid="memo-private-note">
                <Lock size={11} className="shrink-0" /> Private to you until finalised — drafted from the transcript + your notes
              </p>
              <AiSummaryPanel cid={consult.id} canEdit embedded />
            </div>
          )}
          {sideTab === "summary" && (
            <ConsultationSummaryCard consult={consult} isLawyerSide={isLawyerSide} isLive={isLive} isTerminal={isTerminal} isCancelled={isCancelled}
              session={session} canStart={canStart} canComplete={isLawyerSide && !isTerminal && hasLegitimateStart}
              busy={busy} onStart={startConsult} onComplete={completeConsult} />
          )}
          </div>
        </aside>

        {/* Main stage — Phase 1: the conversation (audio/video calls still open over it; docked in Phase 2) */}
        <section className={`order-1 box-border flex w-full max-w-full min-w-0 flex-col min-h-0 bg-white border border-slate-200 rounded-2xl overflow-hidden lg:order-2 lg:col-span-6 xl:col-span-1 ${isTerminal ? "self-start max-h-[70dvh] lg:max-h-full" : "h-[60dvh] lg:h-auto"}`} data-testid="workspace-stage" data-terminal={isTerminal ? "true" : "false"}>
          {isTerminal && (
            <PostConsultationSummary consult={consult} isLawyerSide={isLawyerSide} hasReviewed={hasReviewed} onRate={() => setRatingOpen(true)} />
          )}
          {isTerminal ? (!isCall && conversation) : !isCall ? conversation : (
            inCall ? (
              <DailyCall
                layout="docked"
                consultationId={consult.id}
                type={consult.consultation_type}
                lawyer={consult}
                onEnd={() => setInCall(false)}
                onJoined={isLawyerSide ? reportLawyerJoined : undefined}
                timer={consult.timer}
                onExpired={settleExpiry}
                canGift={isLawyerSide}
                onGift={isLawyerSide ? giftTime : undefined}
              />
            ) : (
              <CallStage consult={consult} isLive={isLive} isCompleted={isCompleted} isLawyerSide={isLawyerSide} hadCall={hadCall} onJoin={() => { setHadCall(true); setInCall(true); }} onExpired={settleExpiry} onGift={isLawyerSide ? giftTime : undefined} onWaitExpired={settleExpiry} />
            )
          )}
        </section>

        {/* Right panel (shared by both sides) — audio/video: Chat | Documents; chat mode: Documents (the chat is the stage) */}
        <RightPanel
          tabs={isCall ? ["chat", "documents"] : ["documents"]}
          active={activeTab}
          onChange={setPanelTab}
          badges={{ chat: activeTab === "chat" ? 0 : unreadChat, documents: activeTab === "documents" ? 0 : (docCount || 0) }}
          className="order-3 box-border h-[50dvh] w-full max-w-full min-w-0 lg:h-auto lg:min-h-0 lg:col-span-3 xl:col-span-1"
          panelRef={panelRef}
        >
          {isCall && activeTab === "chat" && conversation}
          {activeTab === "documents" && (
            <DocumentsPane cid={consult.id} refreshKey={docsKey + messages.length} onCount={setDocCount} />
          )}
        </RightPanel>
      </div>


      <RatingPromptModal
        consultationId={consult.id}
        lawyerName={consult.lawyer_name}
        open={ratingOpen}
        onOpenChange={(v) => {
          setRatingOpen(v);
          if (!v) localStorage.setItem(`adv_rated_${consult.id}`, "1");
        }}
        onSubmitted={() => { setHasReviewed(true); load(); }}
      />

    </div>
  );
}
